/**
 * uiVisibilityEngine.ts
 *
 * Pure, deterministic engine that evaluates metadata-driven UI visibility rules.
 * Consumes the same rule definitions as formRuleEngine but operates in a
 * broader context (entity-level, not just form-level) — supporting field,
 * section, tab, column, and zone visibility/state control.
 *
 * No hardcoded field names or entity-specific logic.
 */

import type { FieldValue } from '../../programs/components/workspace/dynamicFormFieldUtils';

// ═══════════════════════════════════════════════════════════════════════════
//  TYPES
// ═══════════════════════════════════════════════════════════════════════════

/** Full context available to UI visibility rules at evaluation time. */
export interface UiRuleContext {
  entityType: string;
  entityId?: string;
  formData: Record<string, FieldValue>;
  patientData?: Record<string, FieldValue>;
  caseData?: Record<string, FieldValue>;
  workflowStepId?: string;
  workflowStatus?: string;
  roleIds: string[];
  programId: string;
}

export type UiRuleOperator =
  | 'equals'
  | 'not_equals'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'in'
  | 'not_in'
  | 'contains'
  | 'is_empty'
  | 'is_not_empty'
  | 'has_role'
  | 'at_step';

export interface UiRuleCondition {
  /** Source of the value: formData (default), patientData, caseData, context */
  source?: 'formData' | 'patientData' | 'caseData' | 'context';
  /** Key to read from the selected source */
  field: string;
  operator: UiRuleOperator;
  value?: string;
}

export type UiActionType =
  | 'SHOW_FIELD'
  | 'HIDE_FIELD'
  | 'ENABLE_FIELD'
  | 'DISABLE_FIELD'
  | 'SHOW_SECTION'
  | 'HIDE_SECTION'
  | 'SHOW_TAB'
  | 'HIDE_TAB'
  | 'SHOW_COLUMN'
  | 'HIDE_COLUMN'
  | 'SHOW_ZONE'
  | 'HIDE_ZONE'
  | 'SHOW_ACTION'
  | 'HIDE_ACTION'
  | 'SET_READONLY';

export interface UiRuleAction {
  type: UiActionType;
  target: string;
}

export interface UiVisibilityRule {
  id: string;
  name?: string;
  conditionType: 'ALL' | 'ANY';
  conditions: UiRuleCondition[];
  actions: UiRuleAction[];
  enabled: boolean;
  priority?: number;
}

// ═══════════════════════════════════════════════════════════════════════════
//  RESULT
// ═══════════════════════════════════════════════════════════════════════════

export interface UiVisibilityResult {
  hiddenFieldKeys: Set<string>;
  disabledFieldKeys: Set<string>;
  hiddenSectionKeys: Set<string>;
  hiddenTabKeys: Set<string>;
  hiddenColumnKeys: Set<string>;
  hiddenZoneKeys: Set<string>;
  hiddenActionKeys: Set<string>;
  readOnlyFieldKeys: Set<string>;
}

function emptyResult(): UiVisibilityResult {
  return {
    hiddenFieldKeys: new Set(),
    disabledFieldKeys: new Set(),
    hiddenSectionKeys: new Set(),
    hiddenTabKeys: new Set(),
    hiddenColumnKeys: new Set(),
    hiddenZoneKeys: new Set(),
    hiddenActionKeys: new Set(),
    readOnlyFieldKeys: new Set(),
  };
}

// ═══════════════════════════════════════════════════════════════════════════
//  EVALUATOR (pure, deterministic)
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Evaluate UI visibility rules against the given context.
 * Rules are processed in priority order (lower = first).
 * Later rules can override earlier ones (last-write-wins per target).
 */
export function evaluateUiVisibility(
  rules: UiVisibilityRule[],
  context: UiRuleContext,
): UiVisibilityResult {
  const result = emptyResult();

  const sorted = [...rules]
    .filter((r) => r.enabled)
    .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999));

  for (const rule of sorted) {
    if (!matchesConditions(rule, context)) continue;

    for (const action of rule.actions) {
      applyAction(result, action);
    }
  }

  return result;
}

// ═══════════════════════════════════════════════════════════════════════════
//  CONDITION MATCHING
// ═══════════════════════════════════════════════════════════════════════════

function matchesConditions(rule: UiVisibilityRule, ctx: UiRuleContext): boolean {
  if (rule.conditions.length === 0) return true;

  const evaluator = (c: UiRuleCondition) => evaluateCondition(c, ctx);

  return rule.conditionType === 'ALL'
    ? rule.conditions.every(evaluator)
    : rule.conditions.some(evaluator);
}

function evaluateCondition(cond: UiRuleCondition, ctx: UiRuleContext): boolean {
  const raw = resolveValue(cond.source ?? 'formData', cond.field, ctx);
  const rawStr = raw != null ? String(raw) : '';
  const compareVal = cond.value ?? '';

  switch (cond.operator) {
    case 'equals':
      return rawStr.toLowerCase() === compareVal.toLowerCase();
    case 'not_equals':
      return rawStr.toLowerCase() !== compareVal.toLowerCase();
    case 'gt':
      return toNum(rawStr) > toNum(compareVal);
    case 'gte':
      return toNum(rawStr) >= toNum(compareVal);
    case 'lt':
      return toNum(rawStr) < toNum(compareVal);
    case 'lte':
      return toNum(rawStr) <= toNum(compareVal);
    case 'in': {
      const list = compareVal.split(',').map((s) => s.trim().toLowerCase());
      return list.includes(rawStr.toLowerCase());
    }
    case 'not_in': {
      const list = compareVal.split(',').map((s) => s.trim().toLowerCase());
      return !list.includes(rawStr.toLowerCase());
    }
    case 'contains':
      return rawStr.toLowerCase().includes(compareVal.toLowerCase());
    case 'is_empty':
      return rawStr === '' || raw == null;
    case 'is_not_empty':
      return rawStr !== '' && raw != null;
    case 'has_role':
      return ctx.roleIds.some((r) => r.toLowerCase() === compareVal.toLowerCase());
    case 'at_step':
      return (ctx.workflowStepId ?? '').toLowerCase() === compareVal.toLowerCase();
    default:
      return false;
  }
}

function resolveValue(
  source: 'formData' | 'patientData' | 'caseData' | 'context',
  field: string,
  ctx: UiRuleContext,
): FieldValue | undefined {
  switch (source) {
    case 'formData':
      return ctx.formData[field];
    case 'patientData':
      return ctx.patientData?.[field];
    case 'caseData':
      return ctx.caseData?.[field];
    case 'context':
      switch (field) {
        case 'entityType': return ctx.entityType;
        case 'entityId': return ctx.entityId;
        case 'workflowStepId': return ctx.workflowStepId;
        case 'workflowStatus': return ctx.workflowStatus;
        case 'programId': return ctx.programId;
        default: return undefined;
      }
    default:
      return undefined;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//  ACTION APPLICATION
// ═══════════════════════════════════════════════════════════════════════════

function applyAction(result: UiVisibilityResult, action: UiRuleAction): void {
  const { type, target } = action;

  switch (type) {
    case 'HIDE_FIELD':
      result.hiddenFieldKeys.add(target);
      break;
    case 'SHOW_FIELD':
      result.hiddenFieldKeys.delete(target);
      break;
    case 'DISABLE_FIELD':
      result.disabledFieldKeys.add(target);
      break;
    case 'ENABLE_FIELD':
      result.disabledFieldKeys.delete(target);
      break;
    case 'SET_READONLY':
      result.readOnlyFieldKeys.add(target);
      break;
    case 'HIDE_SECTION':
      result.hiddenSectionKeys.add(target);
      break;
    case 'SHOW_SECTION':
      result.hiddenSectionKeys.delete(target);
      break;
    case 'HIDE_TAB':
      result.hiddenTabKeys.add(target);
      break;
    case 'SHOW_TAB':
      result.hiddenTabKeys.delete(target);
      break;
    case 'HIDE_COLUMN':
      result.hiddenColumnKeys.add(target);
      break;
    case 'SHOW_COLUMN':
      result.hiddenColumnKeys.delete(target);
      break;
    case 'HIDE_ZONE':
      result.hiddenZoneKeys.add(target);
      break;
    case 'SHOW_ZONE':
      result.hiddenZoneKeys.delete(target);
      break;
    case 'HIDE_ACTION':
      result.hiddenActionKeys.add(target);
      break;
    case 'SHOW_ACTION':
      result.hiddenActionKeys.delete(target);
      break;
  }
}

// ═══════════════════════════════════════════════════════════════════════════
//  HELPERS
// ═══════════════════════════════════════════════════════════════════════════

function toNum(s: string): number {
  const n = Number(s);
  return isNaN(n) ? 0 : n;
}
